import React, { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import EventTracker from 'src/pendo/EventTracker';
import { useCall } from '../../customer-call-sdk';
import TextButton from '../common/TextButton';
import UserAvatar from '../common/UserAvatar';
import withAuth from '../../hoc/withAuth';

const BootstrapButton = muiStyled(Button)({
    width: '80%',
    heigth: '200px',
    borderRadius: '50px',
    boxShadow: 'none',
    textTransform: 'none',
    fontSize: 16,
    border: '1px solid',
    lineHeight: 1.5,
    backgroundColor: '#0063cc',
    borderColor: '#0063cc',
    fontFamily: [
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
    ].join(','),
    '&:hover': {
        backgroundColor: '#0069d9',
        borderColor: '#0062cc',
        boxShadow: 'none',
    },
    '&:active': {
        boxShadow: 'none',
        backgroundColor: '#0062cc',
        borderColor: '#005cbf',
    },
    '&:focus': {
        boxShadow: '0 0 0 0.1rem rgba(0,123,255,.5)',
    },
});

const StyledOutboundCallConnecting = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 1.5rem;

    .connecting-label {
        color: var(--text-primary);
        font-size: 2.125rem;
        font-weight: bold;
        line-height: 2.5rem;
        text-align: center;
    }
    .message-container {
        display: flex;
        flex-direction: column;
        align-items: center;
    }
    .avatar-container {
        .avatar {
            width: 120px;
            height: 120px;
        }
        .avatar svg {
            width: 80px;
            height: 80px;
        }
    }
    .hold-text {
        font-size: 1.15rem;
        text-align: center;
        margin-top: 1.5rem;
    }
    .queue-text {
        font-size: 1.4rem;
        margin-top: 1.5rem;
        justify-content: center;
        display: flex;
        flex-direction: row;
        align-items: center;
    }
    .positon-text {
        font-weight: bold;
        padding: 5px;
    }
    .actions-container {
        padding: 2rem;
        text-align: center;
    }
    .video-mail {
        display: flex;
        justify-content: center;
    }
    .video-mail-text {
        margin-top: 20px;
        display: flex;
        justify-content: center;
    }
`;
export interface OutboundCallConnectingProps extends React.HTMLAttributes<HTMLDivElement> {}

const OutboundCallConnecting: React.FC<OutboundCallConnectingProps> = ({ ...props }) => {
    const call = useCall();
    const [showElement, setShowElement] = useState(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        setTimeout(function () {
            setShowElement(false);
        }, 15000);
    }, []);

    const handleCancelClick = async () => {
        setIsLoading(true);
        await call.reject();
        EventTracker.trackEvent('cancelCallFromQueue', { position: call.position });
        setIsLoading(false);
    };

    const renderHoldText = () => {
        switch (call.requestType) {
            case 'P2P_OUTBOUND':
                return 'Welcome, please hold for the next available agent';
            case 'VRS_OUTBOUND':
            default:
                return (
                    <div
                        aria-live="polite"
                        aria-label="Please hold for the next available interpreter. If you hang up, you will lose your place in the queue."
                    >
                        Welcome, please hold for the next available interpreter. If you hang up you
                        will lose your place in the queue.
                    </div>
                );
        }
    };

    const openVideoMailAlertDialog = useCallback(() => {
        call.setDialogFlagTrue();
        call.reject();
    }, []);

    return (
        <>
            <StyledOutboundCallConnecting {...props}>
                <h3 className="connecting-label">Connecting...</h3>
                {showElement && call.position && (
                    <div
                        className="queue-text"
                        aria-live="polite"
                        aria-label={`You are now in queue for an interpreter. Your current position is ${call.position}.`}
                    >
                        You are at place
                        <span className="positon-text">{call.position}</span>
                        in the queue.
                    </div>
                )}

                <div className="message-container">
                    <div className="avatar-container">
                        <UserAvatar className="avatar" />
                    </div>

                    <div className="hold-text">{renderHoldText()}</div>
                </div>

                <div className="actions-container">
                    {!showElement && call.requestType === 'P2P_OUTBOUND' && (
                        <>
                            <div className="video-mail-text">
                                <b>Don&apos;t want to wait for Customer Support?</b>
                            </div>
                            <div className="video-mail">
                                <BootstrapButton
                                    variant="contained"
                                    onClick={openVideoMailAlertDialog}
                                    color="primary"
                                    disableRipple
                                    sx={{ margin: 1 }}
                                    aria-label="Send videomail now"
                                >
                                    Send videomail now
                                </BootstrapButton>
                            </div>
                        </>
                    )}
                    <TextButton
                        color="secondary"
                        onClick={handleCancelClick}
                        disabled={isLoading}
                        aria-label="Hang Up"
                    >
                        <b>Hang up</b>
                    </TextButton>
                </div>
            </StyledOutboundCallConnecting>
        </>
    );
};

export default withAuth(OutboundCallConnecting);
